/**
 * Catalog of every field the Create Patient form can collect.
 *
 * Each field sits in one of three states, chosen by the tenant in
 * Settings → Patient Settings:
 *   - required: on the form, must be filled in (`tenant.requiredPatientFields`)
 *   - shown:    on the form, can be left blank (`tenant.defaultPatientFields`)
 *   - hidden:   off the form until staff add it from "Add more details"
 *
 * This catalog is the single source of truth for what the form collects, so the
 * form and the settings preview can never drift.
 */

/** Fields that are always on the form and always required. */
export type PatientLockedFieldKey = "firstName" | "lastName" | "phoneNumber";

export type PatientFormFieldKey =
  | PatientLockedFieldKey
  | "middleName"
  | "email"
  | "dateOfBirth"
  | "gender"
  | "pronouns"
  | "preferredName"
  | "externalId"
  | "leadSource"
  | "preferredProvider"
  | "address"
  | "marketing"
  | "paymentMethod";

export type PatientOptionalFieldKey = Exclude<
  PatientFormFieldKey,
  PatientLockedFieldKey
>;

/**
 * Which fields a caller allows on its copy of the form. Callers that scope the
 * form to a subset (the invoice contact edit) pass an explicit object; omitting
 * a key hides that field. `primaryLocation` isn't in the catalog — the form
 * always shows it to tenants with more than one location — but it is scoped the
 * same way.
 */
export type PatientFieldVisibility = Partial<
  Record<PatientOptionalFieldKey | "primaryLocation", boolean>
>;

export interface PatientFormFieldSpec {
  key: PatientFormFieldKey;
  /** Value stored in `requiredPatientFields` / `defaultPatientFields`. */
  requiredKey: string;
  label: string;
  /** Width in the form's 6-column grid. */
  span: 2 | 3 | 6;
  /** Placeholder the settings preview shows inside the field. */
  previewValue: string;
  /** Which real input primitive the preview renders. Omitted = plain text input. */
  previewControl?: "select" | "phone" | "dob";
  /** Always on the form and always required — tenants can't turn these off. */
  locked?: true;
}

export const PATIENT_FORM_FIELDS: PatientFormFieldSpec[] = [
  {
    key: "firstName",
    requiredKey: "first_name",
    label: "First Name",
    span: 3,
    previewValue: "John",
    locked: true,
  },
  {
    key: "middleName",
    requiredKey: "middle_name",
    label: "Middle Name",
    span: 2,
    previewValue: "Quincy",
  },
  {
    key: "lastName",
    requiredKey: "last_name",
    label: "Last Name",
    span: 3,
    previewValue: "Smith",
    locked: true,
  },
  {
    key: "phoneNumber",
    requiredKey: "phone_number",
    label: "Phone Number",
    span: 6,
    previewValue: "(123) 456-7890",
    previewControl: "phone",
    locked: true,
  },
  {
    key: "email",
    requiredKey: "email",
    label: "Email",
    span: 3,
    previewValue: "example@email.com",
  },
  {
    key: "dateOfBirth",
    requiredKey: "date_of_birth",
    label: "Date of Birth",
    span: 3,
    previewValue: "MM/DD/YYYY",
    previewControl: "dob",
  },
  {
    key: "gender",
    requiredKey: "gender",
    label: "Gender",
    span: 3,
    previewValue: "Select gender",
    previewControl: "select",
  },
  {
    key: "pronouns",
    requiredKey: "pronouns",
    label: "Pronouns",
    span: 3,
    previewValue: "she/her",
  },
  {
    key: "preferredName",
    requiredKey: "preferred_name",
    label: "Preferred Name",
    span: 3,
    previewValue: "Nickname",
  },
  {
    key: "externalId",
    requiredKey: "external_id",
    label: "External ID",
    span: 3,
    previewValue: "Your system's patient ID",
  },
  {
    key: "leadSource",
    requiredKey: "lead_source",
    label: "Lead Source",
    span: 3,
    previewValue: "Select or add lead source",
    previewControl: "select",
  },
  {
    key: "preferredProvider",
    requiredKey: "preferred_provider",
    label: "Preferred Provider",
    span: 3,
    previewValue: "Select a provider",
    previewControl: "select",
  },
  {
    key: "address",
    requiredKey: "address",
    label: "Address",
    span: 6,
    previewValue: "Street, city, state and ZIP",
  },
  {
    key: "marketing",
    requiredKey: "marketing",
    label: "Marketing Preferences",
    span: 6,
    previewValue: "SMS and email opt-in",
    previewControl: "select",
  },
  {
    key: "paymentMethod",
    requiredKey: "payment_method",
    label: "Credit Card on File",
    span: 6,
    previewValue: "Card number, expiry and CVC",
  },
];

/** Fields a tenant can move between required, shown and hidden. */
export const PATIENT_OPTIONAL_FIELDS = PATIENT_FORM_FIELDS.filter(
  (field) => !field.locked,
);

/** Grid classes keyed by span so Tailwind sees the literal class names. */
export const FIELD_SPAN_CLASS: Record<PatientFormFieldSpec["span"], string> = {
  2: "md:col-span-2",
  3: "md:col-span-3",
  6: "md:col-span-6",
};

/**
 * A middle name squeezes First/Middle/Last onto one row; without it the two
 * name fields split the row evenly.
 */
export function nameFieldSpan(hasMiddleName: boolean): 2 | 3 {
  return hasMiddleName ? 2 : 3;
}
